/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

//react imports
import { useEffect, useMemo, useState, useCallback } from 'react';

//react native imports
import { useTranslation } from 'react-i18next';
import {
  View,
  NativeSyntheticEvent,
  NativeScrollEvent,
  Platform,
} from 'react-native';
import { StyleSheet } from 'react-native-unistyles';

//phoenix imports
import TextView from '@airasia-phoenix/react-native/components/TextView';
import { getTextStyle, pd } from '@airasia-phoenix/react-native/utils/utils';
import HorizontalContentCardGroupView from '@airasia-phoenix/react-native/widgets/HorizontalContentCardGroupView';

//ota imports
import Constants from './constants';
import { RecentSearchesCarouselItemProps } from './types';

//hooks
import {
  OriginWithDestinations,
  useRecentSearches,
} from '../../hooks/useRecentSearches';
import { StationDataObject } from '../../types/FlightSearch.types';
import { useCarouselInfiniteScroll } from './useCarouselInfiniteScroll';
import { useRecentSearchesCarouselCards } from './useRecentSearchesCarouselCards';
import { useSuperAppContext } from '../../context/SuperAppContext';
import { formatDateTime } from '../../util/dateUtil';
import { AALocale } from '@airasia-phoenix/react-native/utils';

/**
 * Flattened pair for the carousel
 */
type RecentPair = {
  id: string;
  origin: StationDataObject;
  destination: StationDataObject;
};

/**
 * Card item type: keep phoenix type + add internal _pairId for safe comparisons
 * (We don't rely on `model === pair` anymore.)
 */
type RecentSearchCardItem = RecentSearchesCarouselItemProps & {
  _pairId: string;
};

const stationKey = (s: StationDataObject) =>
  `${s.IsCity ? '1' : '0'}|${s.CityCode}|${s.StationCode}`;

const buildPairId = (origin: StationDataObject, dest: StationDataObject) =>
  `${stationKey(origin)}->${stationKey(dest)}`;

/**
 * Flatten persisted structure:
 * [
 *   { origin, destinations:[d1,d2] },
 *   { origin2, destinations:[d3] }
 * ]
 * ->
 * [ (origin,d1), (origin,d2), (origin2,d3) ]
 */
const flattenOriginsToPairs = (
  origins: OriginWithDestinations[],
): RecentPair[] => {
  const pairs: RecentPair[] = [];

  for (const origin of origins) {
    const dests = origin.destinations ?? [];
    for (const dest of dests) {
      pairs.push({
        id: buildPairId(origin, dest),
        origin,
        destination: dest,
      });
    }
  }

  return pairs;
};

const RecentSearchesCarouselView = () => {
  const { t } = useTranslation('flights');
  const { openModule } = useSuperAppContext();

  // read from persisted React Query
  const { data: recentSearchesStore, isLoading } = useRecentSearches();
  const recentOrigins = recentSearchesStore?.recentSearches;

  const [carouselData, setCarouselData] = useState<RecentSearchCardItem[]>([]);
  const [isAtStart, setIsAtStart] = useState(true);

  const allPairs = useMemo(() => {
    if (!recentOrigins || recentOrigins.length === 0) return [];
    // If you want newest-first, ensure your addRecentSearch mutation already moves updated origin to top.
    // For destinations, if you want newest destination first, store newest at end and reverse here if needed.
    return flattenOriginsToPairs(recentOrigins);
  }, [recentOrigins]);

  const handleCardPress = useCallback((pair: RecentPair) => {
    if (__DEV__) {
      console.log('Selected pair:', pair);
    }
    if (Platform.OS !== 'web') {
      const originData = pair.origin as OriginWithDestinations;
      const departDateRaw = originData.departDate;
      const returnDateRaw = originData.returnDate;
      const passengersRaw = originData.passengers;

      const tripType = returnDateRaw ? 'R' : 'O';

      const departJourneyDate = departDateRaw
        ? new Date(departDateRaw)
        : new Date();
      const returnJourneyDate = returnDateRaw
        ? new Date(returnDateRaw)
        : undefined;

      const origin = pair.origin.CityCode || pair.origin.StationCode;
      const destination =
        pair.destination.CityCode || pair.destination.StationCode;
      const adult = passengersRaw?.adultCount ?? 1;
      const child = passengersRaw?.childCount ?? 0;
      const infant = passengersRaw?.infantCount ?? 0;
      const isOC = !!pair.origin.IsCity;
      const isDC = !!pair.destination.IsCity;

      if (tripType === 'R' && returnJourneyDate) {
        openModule?.({
          widget: 'customerJourney',
          deeplink: `airasia://trips/createnewtrip?origin=${origin}&destination=${destination}&departDate=${formatDateTime(
            {
              date: departJourneyDate,
              locale: AALocale.EN_GB,
            },
          )}&tripType=R&returnDate=${formatDateTime({
            date: returnJourneyDate,
            locale: AALocale.EN_GB,
          })}&createNewTrip=true&adult=${adult}&child=${child}&infant=${infant}&isDC=${isDC}&isOC=${isOC}`,
        });
      } else {
        openModule?.({
          widget: 'customerJourney',
          deeplink: `airasia://trips/createnewtrip?origin=${origin}&destination=${destination}&departDate=${formatDateTime(
            {
              date: departJourneyDate,
              locale: AALocale.EN_GB,
            },
          )}&tripType=O&createNewTrip=true&adult=${adult}&child=${child}&infant=${infant}&isDC=${isDC}&isOC=${isOC}`,
        });
      }
    }
  }, []);

  const mapToCards = useRecentSearchesCarouselCards(
    setCarouselData,
    handleCardPress,
  );

  const { handleScroll, loadedCountRef, reset } =
    useCarouselInfiniteScroll<RecentPair>({
      allItems: allPairs,
      loadCount: Constants.SUBSEQUENT_LOAD_IMAGE_COUNT,
      onLoadMore: (pairs) => {
        setCarouselData((prev) => [...prev, ...mapToCards(pairs)]);
      },
    });

  const handleCarouselScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const offsetX = event.nativeEvent.contentOffset.x;

      // Detect if fully at start
      setIsAtStart(offsetX <= 0);

      // Keep infinite scroll working
      handleScroll(event);
    },
    [handleScroll], // important dependency
  );

  useEffect(() => {
    if (isLoading || !allPairs.length) return;

    reset();

    const initialPairs = allPairs.slice(0, Constants.INITIAL_LOAD_IMAGE_COUNT);

    setCarouselData(mapToCards(initialPairs));
    loadedCountRef.current = initialPairs.length;
  }, [isLoading, allPairs, mapToCards, reset, loadedCountRef]);

  if (isLoading || !allPairs.length) return null;

  return (
    <View>
      <View style={styles.titleWrapper}>
        <TextView
          text={t('flightsSearchPage.main.yourRecentSearches')}
          style={[
            getTextStyle(
              pd.sys.typescale.body_text_medium,
              pd.sys.colour.content_primary,
            ),
          ]}
        />
      </View>

      <View>
        <HorizontalContentCardGroupView
          orientation={Constants.CAROUSEL_ORIENTATION}
          contentCardProps={
            carouselData as unknown as RecentSearchesCarouselItemProps[]
          }
          viewHeight={Constants.CAROUSEL_HEIGHT}
          //@ts-expect-error flat list props render item already passed.
          flatListProps={{
            decelerationRate: Constants.SCROLL_SPEED,
            showsHorizontalScrollIndicator: false,
            onScroll: handleCarouselScroll,
            scrollEventThrottle: 16,
            contentContainerStyle: {
              paddingLeft: isAtStart ? 7 : 0,
            },
          }}
        />
      </View>
    </View>
  );
};

export default RecentSearchesCarouselView;

const styles = StyleSheet.create({
  titleWrapper: { marginBottom: 10, marginHorizontal: 16 },
  scrollContainer: {
    left: 7,
  },
});
